/**
 * Show hours reader (no-sale alerts, owner 22 Oct 2026).
 *
 * Reads a show's hours from pasted text (show sites, emails, exhibitor
 * manuals) and from a typed time ("10a", "6:30p", "18:30", "noon"), for the
 * per-date "Show hours" list of the show form. Pure functions: no React, no
 * store, no clock.
 *
 * Times are minutes from local midnight inside this file and milliseconds
 * from local midnight in what it returns (the unit of Events.schedule).
 *
 * How pasted text is read — THE STRICT GRAMMAR (owner / coordinator, 23 Oct,
 * final design). A filled row must be certain; anything else is "unclear"
 * (with a reason, no From/To) or "unmentioned" — never a guess.
 * 1. Clauses: the text splits into clauses at line ends, ";", "|", "•"
 *    and sentence ends (". ", "! ", "? "; "a.m.", "Fri.", "Oct." don't end
 *    one).
 * 2. A clause FILLS rows only if it is exactly
 *        [DAYS] + [TIME]   or   [TIME] + [DAYS]
 *    repeated, all in the same order within a comma part ("Fri 10-6 Sat
 *    11-5", "10-6 Fri & Sat, 11-5 Sun"); a comma part that is only days
 *    joins the next ("Fri, Sat & Sun 10-6").
 *      DAYS = a weekday (full name, plural, 3-letter, Tue / Thur / Thurs),
 *             a weekday range ("Fri–Sun", "Fri to Sun"), a date ("Oct 23",
 *             "October 23rd, 2026", "23 Oct", "10/23", "10/23/2026",
 *             "2026-10-23"), a date range ("Oct 23–25", "10/23 - 10/25"),
 *             or "daily" / "every day" / "each day" (the every-day default).
 *             A weekday written next to a date must be that date's weekday.
 *      TIME = a time range ("10am–6pm", "10-6", "10:00–18:00", "noon to
 *             5 pm", "6pm–midnight") that opens after midnight and closes
 *             after it opens; or "closed".
 *    Connectors only: , & and : - – — / to (and spaces). A clause may start
 *    with "Hours", "Open", "Show hours" or "Public hours" (a no-op prefix).
 * 3. EVERYTHING ELSE makes the clause unclear: it fills nothing; the dates
 *    it names are flagged ("Not sure about …"); with a time and no day,
 *    every date is. That includes except, until, till, from, through,
 *    only, first, last, final, opening, closing, other, not, no, but,
 *    after, before, late, early, same, "closes at", "opens at", short names
 *    (M/W/F, Tu, Th, Sa, Su, S-S), weekends / weekdays, holidays, "the
 *    11th", brackets, any number that isn't a time or a date, any other
 *    word; a split day ("10–2 & 4–8"); "Friday daily"; days left without
 *    a time ("Saturday 10-6 and Sunday"). A word that looks like a
 *    misspelled day ("Fridy", "Snuday") flags every date.
 * 4. No am/pm on either end ("10-6"): see noMeridiemUnsure — read only by
 *    the 8–11 / noon convention, else unclear.
 * 5. Setup, move-in, load-in, teardown and load-out clauses go to notes
 *    only, never hours; a part of such a clause that may be show hours is
 *    flagged.
 * 6. ONE RESOLVER: a date's own rules (weekday, date, date range) must all
 *    agree — two different times or "closed" vs a time → unclear. They
 *    replace the every-day default ("Daily 10-6, Sunday 11-5"); two
 *    different every-day times → unclear. No other precedence.
 * 7. A date the text gives no hours for comes back "unmentioned" (the form
 *    asks "Closed?").
 * 8. Times are exact, on a 5-minute grid (owner, 23 Oct: NSTA opens at
 *    10:50 am). A time off the grid ("10:52") is refused with
 *    OFF_GRID_MESSAGE — never rounded.
 */

export const MINUTE_MS = 60_000;
const DAY_MINUTES = 24 * 60;
// The time pickers' last option (11:59 PM): a show closing at midnight.
const LAST_MINUTE = DAY_MINUTES - 1;

/** Show hours are kept to the minute, on this grid (owner, 23 Oct). */
export const SHOW_HOURS_STEP_MINUTES = 5;
/** What a typed or pasted time off the 5-minute grid gets: refused. */
export const OFF_GRID_MESSAGE = 'Use 5-minute times, e.g. 10:50';

/** On the 5-minute grid (or the 11:59 pm "midnight" close). */
const onGrid = (minutes: number) =>
  minutes === LAST_MINUTE || minutes % SHOW_HOURS_STEP_MINUTES === 0;

export type ShowHoursStatus = 'filled' | 'unclear' | 'unmentioned';

export interface ParsedShowDay {
  to?: number;
  date: string;
  from?: number;
  reason?: string;
  dayOff?: boolean;
  status: ShowHoursStatus;
}

export interface ShowHoursParseResult {
  notes: string[];
  unread: string[];
  days: ParsedShowDay[];
}

type Meridiem = 'am' | 'pm' | null;

interface RawTime {
  hour: number;
  is24: boolean;
  minute: number;
  meridiem: Meridiem;
  special?: 'noon' | 'midnight';
}

const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHORT_MONTH = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// ---------------------------------------------------------------- dates --

const pad = (n: number) => String(n).padStart(2, '0');

export const isoDate = (year: number, month: number, day: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

const parseIso = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
};

/** Day of the week of a YYYY-MM-DD date (0 = Sunday), time-zone free. */
export const weekdayOf = (iso: string) => {
  const { y, m, d } = parseIso(iso);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

const dayNumber = (iso: string) => {
  const { y, m, d } = parseIso(iso);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
};

const fromDayNumber = (n: number) => {
  const date = new Date(n * 86_400_000);
  return isoDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate()
  );
};

const validDay = (year: number, month: number, day: number) => {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/** "Fri 24 Oct" for a YYYY-MM-DD date. */
export const formatShowDate = (iso: string) => {
  const { m, d } = parseIso(iso);
  return `${SHORT_WEEKDAY[weekdayOf(iso)]} ${d} ${SHORT_MONTH[m - 1]}`;
};

/** "10:00 AM" for minutes from midnight. */
const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
};

// ---------------------------------------------------------------- times --

const MERIDIEM_RE = '(?:a\\.?\\s?m\\.?|p\\.?\\s?m\\.?|a|p)(?![a-z])';
const CLOCK_RE = `(?:(?:12\\s*)?noon|(?:12\\s*)?midnight|\\d{1,2}(?:[:.]\\d{2})?\\s*(?:${MERIDIEM_RE})?)`;
const DASH_RE = '(?:-|–|—|‒|―|~|to|until|till|til|thru|through)';

const readRawTime = (text: string): RawTime | undefined => {
  const value = text.trim().toLowerCase();
  if (/noon/.test(value)) {
    return {
      hour: 12,
      minute: 0,
      is24: false,
      meridiem: 'pm',
      special: 'noon',
    };
  }
  if (/midnight/.test(value)) {
    return {
      hour: 0,
      minute: 0,
      is24: false,
      meridiem: 'am',
      special: 'midnight',
    };
  }
  const match = value.match(
    /^(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?\s?m\.?|p\.?\s?m\.?|a|p)?$/
  );
  if (!match) {
    return undefined;
  }
  const hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem: Meridiem = match[3]
    ? match[3].startsWith('a')
      ? 'am'
      : 'pm'
    : null;
  if (minute > 59) {
    return undefined;
  }
  if (meridiem) {
    if (hour < 1 || hour > 12) {
      return undefined;
    }
    return { hour, minute, meridiem, is24: false };
  }
  if (hour > 24) {
    return undefined;
  }
  const is24 = hour > 12 || hour === 0 || /^0\d/.test(match[1]);
  return { hour, is24, minute, meridiem: null };
};

const withMeridiem = (raw: RawTime, meridiem: 'am' | 'pm') =>
  (raw.hour % 12) * 60 + raw.minute + (meridiem === 'pm' ? 720 : 0);

const literal24 = (raw: RawTime) => (raw.hour % 24) * 60 + raw.minute;

/** An opening time with nothing else to go by: 7–11 morning, 12–6 afternoon. */
const resolveOpening = (raw: RawTime) => {
  if (raw.special === 'noon') {
    return 720;
  }
  if (raw.special === 'midnight') {
    return 0;
  }
  if (raw.meridiem) {
    return withMeridiem(raw, raw.meridiem);
  }
  if (raw.is24) {
    return literal24(raw);
  }
  return raw.hour >= 7 && raw.hour <= 11
    ? withMeridiem(raw, 'am')
    : withMeridiem(raw, 'pm');
};

/**
 * A closing time: the first reading after the opening time. Undefined when
 * the show would close after midnight (not supported: the day ends at
 * 11:59 PM).
 */
const resolveClosing = (raw: RawTime, after?: number) => {
  if (raw.special === 'noon') {
    return after === undefined || after < 720 ? 720 : undefined;
  }
  if (raw.special === 'midnight') {
    return LAST_MINUTE;
  }
  if (raw.meridiem || raw.is24) {
    let minutes = raw.meridiem
      ? withMeridiem(raw, raw.meridiem)
      : literal24(raw);
    if (raw.hour === 24 || (raw.meridiem === 'am' && raw.hour === 12)) {
      minutes = LAST_MINUTE;
    }
    if (after !== undefined && minutes <= after) {
      return undefined;
    }
    return minutes;
  }
  if (after === undefined) {
    return raw.hour === 12 ? 720 : withMeridiem(raw, 'pm');
  }
  const candidates = [withMeridiem(raw, 'am'), withMeridiem(raw, 'pm')];
  return candidates.find((minutes) => minutes > after);
};

interface RangeResult {
  to?: number;
  from?: number;
  error?: string;
}

const PAST_MIDNIGHT = 'Closes after midnight — enter the hours by hand';

const resolveRange = (fromText: string, toText: string): RangeResult => {
  const from = readRawTime(fromText);
  const to = readRawTime(toText);
  if (!from || !to) {
    return { error: 'Times not understood' };
  }
  let opening: number;
  if (from.is24 || to.is24) {
    opening = from.special ? resolveOpening(from) : literal24(from);
    const closing = to.special ? resolveClosing(to) : literal24(to);
    if (closing === undefined || closing <= opening) {
      return to.hour === 24 || to.special === 'midnight'
        ? { from: opening, to: LAST_MINUTE }
        : { from: opening, error: PAST_MIDNIGHT };
    }
    return { to: closing, from: opening };
  }
  if (!from.meridiem && !from.special && to.meridiem && !to.special) {
    // "10–6pm": the opening takes the closing's half of the day when that
    // still opens first ("1–6pm"), else the other half ("10–6pm").
    const closing = withMeridiem(to, to.meridiem);
    const same = withMeridiem(from, to.meridiem);
    opening =
      same < closing
        ? same
        : withMeridiem(from, to.meridiem === 'am' ? 'pm' : 'am');
    if (to.meridiem === 'am' && to.hour === 12) {
      return { from: opening, to: LAST_MINUTE };
    }
  } else {
    opening = resolveOpening(from);
  }
  const closing = resolveClosing(to, opening);
  if (closing === undefined) {
    return { from: opening, error: PAST_MIDNIGHT };
  }
  return { to: closing, from: opening };
};

/** Minutes as they are (never rounded), within the day. */
const exactMs = (minutes: number) => Math.min(LAST_MINUTE, minutes) * MINUTE_MS;

// A typed time in minutes, exactly as written (or undefined).
const typedMinutes = (
  text: string,
  role: 'to' | 'from',
  after?: number
): number | undefined => {
  let value = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!value) {
    return undefined;
  }
  const compact = value.match(/^(\d{3,4})\s*([ap]\.?m?\.?)?$/);
  if (compact) {
    const digits = compact[1].padStart(4, '0');
    value = `${digits.slice(0, 2)}:${digits.slice(2)}${compact[2] ? ` ${compact[2]}` : ''}`;
  }
  const raw = readRawTime(value);
  if (!raw) {
    return undefined;
  }
  return role === 'from'
    ? resolveOpening(raw)
    : resolveClosing(
        raw,
        after === undefined ? undefined : Math.floor(after / MINUTE_MS)
      );
};

/**
 * A time typed into a From or To box: "10", "10a", "10am", "10:00 AM",
 * "6:30p", "10:50", "18:30", "1830", "noon", "midnight". Returns
 * milliseconds from midnight, exactly as typed, or undefined when it is not
 * a time or is off the 5-minute grid ("10:52": see typedTimeProblem). A
 * bare hour reads as a show would: From 7–11 morning and 12–6 afternoon;
 * To the first reading after `after` (the From value, in ms), else the
 * afternoon.
 */
export const parseTimeInput = (
  text: string,
  role: 'to' | 'from' = 'from',
  after?: number
): number | undefined => {
  const minutes = typedMinutes(text, role, after);
  return minutes === undefined || !onGrid(minutes)
    ? undefined
    : exactMs(minutes);
};

// A typed range in minutes, exactly as written (or undefined).
const typedRangeMinutes = (
  text: string
): { to: number; from: number } | undefined => {
  const match = text
    .trim()
    .toLowerCase()
    .match(new RegExp(`^(${CLOCK_RE})\\s*${DASH_RE}\\s*(${CLOCK_RE})$`));
  if (!match) {
    return undefined;
  }
  const range = resolveRange(match[1], match[2]);
  if (range.error || range.from === undefined || range.to === undefined) {
    return undefined;
  }
  return { to: range.to, from: range.from };
};

/**
 * A range typed or pasted into one time box: "10-6", "10am – 6pm",
 * "10:50am-6pm", "10:00–18:00". Returns both ends in ms, exactly as typed,
 * or undefined (also when an end is off the 5-minute grid).
 */
export const parseRangeInput = (
  text: string
): { to: number; from: number } | undefined => {
  const range = typedRangeMinutes(text);
  if (!range || !onGrid(range.from) || !onGrid(range.to)) {
    return undefined;
  }
  return { to: exactMs(range.to), from: exactMs(range.from) };
};

/**
 * Why a typed time or range is refused, when it reads as one but is off
 * the 5-minute grid ("10:52", "10:52am-6pm"): OFF_GRID_MESSAGE. Undefined
 * otherwise (a good time, or not a time at all).
 */
export const typedTimeProblem = (
  text: string,
  role: 'to' | 'from' = 'from',
  after?: number
): string | undefined => {
  const minutes = typedMinutes(text, role, after);
  if (minutes !== undefined) {
    return onGrid(minutes) ? undefined : OFF_GRID_MESSAGE;
  }
  const range = typedRangeMinutes(text);
  return range && (!onGrid(range.from) || !onGrid(range.to))
    ? OFF_GRID_MESSAGE
    : undefined;
};

// Words that change a day's hours; on a day line with no readable time.
const CHANGE_WORDS_RE =
  /\b(?:same(?:\s+(?:hours?|times?|as\b[^.;]*))?\b|as\s+above|ditto|clos(?:e|es|ed|ing)\s+(?:early|late)|opens?\s+(?:early|late)|early\s+clos\w*|late\s+open\w*|(?:closes?|opens?|until|till)\b[^.;]*\d)/i;

/** Edit distance with swaps (Damerau–Levenshtein, optimal string). */
const editDistance = (a: string, b: string) => {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) =>
      i === 0 ? j : j === 0 ? i : 0
    )
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
};

/** An unknown word that is probably a misspelled day ("Fridy", "Snuday"). */
const looksLikeADay = (word: string) => {
  const w = word.toLowerCase().replace(/s$/, '');
  return w.length >= 4 && WEEKDAYS.some((day) => editDistance(w, day) <= 2);
};

/** Words of a clause outside its tokens (not weekday / month labels). */
const leftoverWords = (line: string, tokens: Token[]) => {
  const labelWord = new RegExp(`^(?:${WEEKDAY_RE}|${MONTH_RE})$`, 'i');
  let rest = line;
  tokens.forEach((token) => {
    rest =
      rest.slice(0, token.start) +
      ' '.repeat(token.end - token.start) +
      rest.slice(token.end);
  });
  return (rest.match(/[a-z][a-z']+/gi) ?? []).filter(
    (word) => !labelWord.test(word)
  );
};

const SETUP_OR_SHOW = 'Setup or show hours? — check the text';

// The longest piece of a line a reason quotes: a long line is cut (at a
// word), so the row's note stays on one line beside the boxes on an iPad.
const QUOTE_MAX = 24;

/** A text line as a reason names it: "Fri 10:52am-6pm" (long lines cut). */
export const quoteLine = (line: string) => {
  const text = line.trim().replace(/\s+/g, ' ');
  if (text.length <= QUOTE_MAX) {
    return `"${text}"`;
  }
  let cut = text.slice(0, QUOTE_MAX - 1);
  const space = cut.lastIndexOf(' ');
  if (text[cut.length] !== ' ' && space > QUOTE_MAX / 2) {
    cut = cut.slice(0, space);
  }
  return `"${cut.trimEnd()}…"`;
};

// "and 2 more lines" never splits across lines of the note.
const NBSP = '\u00a0';

/**
 * The reason for a bad line, naming it (coordinator, 23 Oct): a time
 * problem reads '"Fri 10:52am-6pm": use 5-minute times, e.g. 10:50'; a word
 * or binding problem 'Not sure about "Fri 10-6 extra": "extra" is not read'.
 */
const aboutLine = (line: string, reason: string) => {
  if (/^"|^which days|^no day/.test(reason)) {
    return `Not sure about ${quoteLine(line)}: ${reason}`;
  }
  // "Fri doesn't match Sat 24 Oct" keeps its day's capital.
  const tail = /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)/.test(reason)
    ? reason
    : `${reason[0].toLowerCase()}${reason.slice(1)}`;
  return `${quoteLine(line)}: ${tail}`;
};

/** Lines named in one reason: the first, then "and N more lines". */
const namedLines = (lines: string[]) => {
  const rest = lines.length - 1;
  if (rest <= 0) {
    return quoteLine(lines[0]);
  }
  if (rest === 1) {
    return `${quoteLine(lines[0])} and ${quoteLine(lines[1])}`;
  }
  return `${quoteLine(lines[0])} and${NBSP}${rest}${NBSP}more${NBSP}lines`;
};

// ------------------------------------------------------- no am / pm --

const NO_MERIDIEM = 'No am/pm — check the hours';

/**
 * A pasted range with no am/pm on either end ("10-6") is read only when
 * the reading is certain by show convention:
 * - opening 8–11 → morning, and the close is then ≤ the opening hour (or
 *   12, noon) → afternoon: "10-6", "9-5", "11-5", "10-10" (10 am – 10 pm),
 *   "10-12" (10 am – noon). "8-10" or "9-11" could be morning or evening
 *   → unclear.
 * - opening 12 → noon, close 1–11 → afternoon: "12-8", "12-9".
 * - opening 1–7 ("7-11", "5-9", "4-9") → unclear (no context makes it an
 *   evening: the strict grammar has no evening words).
 * One explicit end: "10am-6" is read only when one half of the day fits
 * the bare close ("7am-11" could be 11 am or 11 pm → unclear); "10-6pm"
 * keeps the old rule (the opening takes the close's half when it still
 * opens first, "1-6pm" = 1 pm, "10-6pm" = 10 am).
 * 24-hour times ("10:00-18:00", "09-17"), noon and midnight are not
 * affected. Returns undefined when the normal reading applies.
 */
const noMeridiemUnsure = (
  fromText: string,
  toText: string
): 'unsure' | undefined => {
  const from = readRawTime(fromText);
  const to = readRawTime(toText);
  if (
    from &&
    to &&
    // "10:00-18:00" is 24-hour on both ends.
    !fromText.includes(':') &&
    !fromText.includes('.') &&
    !from.meridiem &&
    !from.special &&
    !from.is24 &&
    to.is24 &&
    to.hour !== 24 &&
    to.hour !== 0
  ) {
    // "1-20", "6-22": a 12-hour opening and a 24-hour close.
    return 'unsure';
  }
  if (!from || !to || from.is24 || to.is24 || to.special) {
    return undefined;
  }
  if (!to.meridiem && (from.meridiem || from.special === 'noon')) {
    const opening = resolveOpening(from);
    const both = [withMeridiem(to, 'am'), withMeridiem(to, 'pm')].filter(
      (minutes) => minutes > opening
    );
    return both.length > 1 ? 'unsure' : undefined;
  }
  if (from.meridiem || from.special || to.meridiem) {
    return undefined;
  }
  const s = from.hour;
  const e = to.hour;
  if (s >= 8 && s <= 11) {
    return e <= s || e === 12 ? undefined : 'unsure';
  }
  if (s === 12) {
    return e >= 1 && e <= 11 ? undefined : 'unsure';
  }
  return 'unsure';
};

/**
 * A bare "N-M" (no colon, no am/pm) that is a real month-day which is a
 * show date, or the date of the weekday written just before it ("Friday
 * 10-23"), is a DATE, not hours.
 */
const dashDateOf = (m: RegExpExecArray, ctx: Context) => {
  const match = m[0].match(/^(\d{1,2})\s*[-–—]\s*(\d{1,2})$/);
  if (!match) {
    return undefined;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12) {
    return undefined;
  }
  const iso = buildDate(ctx, month, day);
  if (!iso) {
    return undefined;
  }
  const before = ctx.line
    .slice(0, m.index)
    .match(new RegExp(`(${WEEKDAY_RE})[\\s,]*$`, 'i'));
  if (ctx.dateSet.has(iso)) {
    return iso;
  }
  return before && weekdayIndex(before[1]) === weekdayOf(iso) ? iso : undefined;
};

// --------------------------------------------------------------- tokens --

type Level = 0 | 1 | 2;

interface RefToken {
  end: number;
  kind: 'ref';
  level: Level;
  start: number;
  /** Show dates it covers. */
  dates: string[];
  /** Specific calendar dates it names (show dates or not), for notes. */
  named: string[];
  /** A single weekday name: next to a full date it only labels it. */
  label?: boolean;
  /** "Last day", "first day": an explicit change for that date. */
  binding?: boolean;
  /** Dates of the reference that are not show dates (for "unread"). */
  outside: string[];
  isWeekday?: boolean;
}

type Action =
  | { kind: 'closed' }
  | { raw: string; kind: 'open' }
  | { raw: string; kind: 'close' }
  | { to?: number; kind: 'range'; from?: number; error?: string };

interface ActionToken {
  end: number;
  start: number;
  action: Action;
  kind: 'action';
}

interface ExceptToken {
  end: number;
  start: number;
  kind: 'except';
}

type Token = RefToken | ActionToken | ExceptToken;

const WEEKDAY_RE =
  '(?:sun(?:day)?s?|mon(?:day)?s?|tue(?:s(?:day)?)?s?|wed(?:nesday)?s?|thu(?:r(?:s(?:day)?)?)?s?|fri(?:day)?s?|sat(?:urday)?s?)\\.?';
const MONTH_RE =
  '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\.?';
const ORD_RE = '(?:st|nd|rd|th)?';
const YEAR_RE = '(?:,?\\s*(\\d{4}))?';

const SHORT_DAYS: Record<string, number> = {
  F: 5,
  M: 1,
  W: 3,
  Sa: 6,
  Su: 0,
  Th: 4,
  Tu: 2,
};

/** nth weekday of a month (n = -1: the last one). */
const nthWeekday = (
  year: number,
  month: number,
  weekday: number,
  n: number
) => {
  if (n > 0) {
    const first = weekdayOf(isoDate(year, month, 1));
    return isoDate(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7);
  }
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const last = weekdayOf(isoDate(year, month, days));
  return isoDate(year, month, days - ((last - weekday + 7) % 7));
};

/** A US holiday named in show text, in a given year. */
const holidayDate = (name: string, year: number): string | undefined => {
  const n = name.toLowerCase();
  if (n.startsWith('thanksgiving')) {
    return nthWeekday(year, 11, 4, 4);
  }
  if (/christmas\s+eve/.test(n)) {
    return isoDate(year, 12, 24);
  }
  if (/christmas|xmas/.test(n)) {
    return isoDate(year, 12, 25);
  }
  if (/new\s+year'?s\s+eve/.test(n)) {
    return isoDate(year, 12, 31);
  }
  if (/new\s+year/.test(n)) {
    return isoDate(year, 1, 1);
  }
  if (/independence|july/.test(n)) {
    return isoDate(year, 7, 4);
  }
  if (n.startsWith('labor')) {
    return nthWeekday(year, 9, 1, 1);
  }
  if (n.startsWith('memorial')) {
    return nthWeekday(year, 5, 1, -1);
  }
  if (n.startsWith('veterans')) {
    return isoDate(year, 11, 11);
  }
  return undefined;
};

const weekdayIndex = (text: string) =>
  WEEKDAYS.findIndex((day) => text.toLowerCase().startsWith(day.slice(0, 3)));

const monthIndex = (text: string) =>
  MONTHS.findIndex((month) => text.toLowerCase().startsWith(month)) + 1;

interface Context {
  /** The clause being read (for the no-am/pm rule). */
  line: string;
  dates: string[];
  lastDay: number;
  firstDay: number;
  baseYear: number;
  dateSet: Set<string>;
  /** Weekday names next to a date ("Friday, Oct 23"): checked to match. */
  labels: {
    end: number;
    day: number;
    start: number;
    date: RefToken;
  }[];
}

/** The year of a month/day with no year: the one nearest the show. */
const inferYear = (ctx: Context, month: number, day: number) => {
  const candidates = [ctx.baseYear - 1, ctx.baseYear, ctx.baseYear + 1]
    .filter((year) => validDay(year, month, day))
    .map((year) => {
      const n = dayNumber(isoDate(year, month, day));
      const distance =
        n < ctx.firstDay
          ? ctx.firstDay - n
          : n > ctx.lastDay
            ? n - ctx.lastDay
            : 0;
      return { year, distance };
    });
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates[0]?.year;
};

const makeDateRef = (
  ctx: Context,
  start: number,
  end: number,
  isoDates: string[]
): RefToken => ({
  end,
  start,
  level: 2,
  kind: 'ref',
  named: isoDates,
  dates: isoDates.filter((date) => ctx.dateSet.has(date)),
  outside: isoDates.filter((date) => !ctx.dateSet.has(date)),
});

const spanDates = (fromIso: string, toIso: string) => {
  const first = dayNumber(fromIso);
  const last = dayNumber(toIso);
  if (last < first || last - first > 62) {
    return [fromIso];
  }
  const result: string[] = [];
  for (let n = first; n <= last; n++) {
    result.push(fromDayNumber(n));
  }
  return result;
};

const buildDate = (
  ctx: Context,
  month: number,
  day: number,
  year?: string
): string | undefined => {
  let y = year ? Number(year) : inferYear(ctx, month, day);
  if (year && year.length === 2) {
    y = 2000 + Number(year);
  }
  if (!y || !validDay(y, month, day)) {
    return undefined;
  }
  return isoDate(y, month, day);
};

interface Matcher {
  re: RegExp;
  make: (match: RegExpExecArray, ctx: Context) => Token | undefined;
}

const weekdayDates = (ctx: Context, weekdays: number[]) =>
  ctx.dates.filter((date) => weekdays.includes(weekdayOf(date)));

const weekdayRef = (
  ctx: Context,
  match: RegExpExecArray,
  weekdays: number[],
  level: Level = 1
): RefToken => ({
  level,
  named: [],
  outside: [],
  kind: 'ref',
  isWeekday: true,
  start: match.index,
  dates: weekdayDates(ctx, weekdays),
  end: match.index + match[0].length,
});

const actionToken = (match: RegExpExecArray, action: Action): ActionToken => ({
  action,
  kind: 'action',
  start: match.index,
  end: match.index + match[0].length,
});

const RANGE_SEP = `\\s*${DASH_RE}\\s*`;

// Order matters: earlier matchers claim their text first.
const MATCHERS: Matcher[] = [
  // 2026-10-24
  {
    re: /\b(\d{4})-(\d{2})-(\d{2})\b/g,
    make: (m, ctx) => {
      const date = buildDate(ctx, Number(m[2]), Number(m[3]), m[1]);
      return date
        ? makeDateRef(ctx, m.index, m.index + m[0].length, [date])
        : undefined;
    },
  },
  // Oct 23 & 24 · October 23 and 24: two dates of one month
  {
    re: new RegExp(
      `\\b(${MONTH_RE})\\s*(\\d{1,2})${ORD_RE}\\s*(?:&|and)\\s*(\\d{1,2})${ORD_RE}${YEAR_RE}(?!\\d|[:.]\\d|\\s*(?:a\\.?m|p\\.?m|[ap]\\b))`,
      'gi'
    ),
    make: (m, ctx) => {
      const month = monthIndex(m[1]);
      const first = buildDate(ctx, month, Number(m[2]), m[4]);
      const second = buildDate(ctx, month, Number(m[3]), m[4]);
      return first && second
        ? makeDateRef(ctx, m.index, m.index + m[0].length, [first, second])
        : undefined;
    },
  },
  // Oct 24 – Nov 2, 2026 · October 24th through 26th · Oct 24, 2026
  {
    re: new RegExp(
      `\\b(${MONTH_RE})\\s*(\\d{1,2})${ORD_RE}(?!\\d|[:.]\\d)(?:${RANGE_SEP}(?:(${MONTH_RE})\\s*)?(\\d{1,2})${ORD_RE}(?!\\d|[:.]\\d))?${YEAR_RE}(?!\\d|[:.]\\d|\\s*(?:a\\.?m|p\\.?m|[ap]\\b))`,
      'gi'
    ),
    make: (m, ctx) => {
      // "1 Nov 10-6", "23 Oct 10-6": the day comes first; the number after
      // the month is a time, so the day-first reading takes it.
      if (
        /\d{1,2}(?:st|nd|rd|th)?\s*(?:of\s+)?$/i.test(
          ctx.line.slice(0, m.index)
        )
      ) {
        return undefined;
      }
      const month = monthIndex(m[1]);
      const first = buildDate(ctx, month, Number(m[2]), m[5]);
      if (!first) {
        return undefined;
      }
      let dates = [first];
      if (m[4]) {
        let toMonth = m[3] ? monthIndex(m[3]) : month;
        if (!m[3] && Number(m[4]) < Number(m[2])) {
          // "Oct 30-1": the range runs into the next month.
          toMonth = (month % 12) + 1;
        }
        const last = buildDate(ctx, toMonth, Number(m[4]), m[5]);
        if (last) {
          dates = spanDates(first, last);
        }
      }
      return makeDateRef(ctx, m.index, m.index + m[0].length, dates);
    },
  },
  // 24 Oct · 24–26 October · 24th of October
  {
    re: new RegExp(
      `\\b(\\d{1,2})${ORD_RE}(?:${RANGE_SEP}(\\d{1,2})${ORD_RE})?\\s*(?:of\\s+)?(${MONTH_RE})(?![a-z])(?:${RANGE_SEP}(\\d{1,2})${ORD_RE}\\s*(?:of\\s+)?(${MONTH_RE})(?![a-z]))?${YEAR_RE}`,
      'gi'
    ),
    make: (m, ctx) => {
      const month = monthIndex(m[3]);
      const first = buildDate(ctx, month, Number(m[1]), m[6]);
      if (!first) {
        return undefined;
      }
      const last = m[4]
        ? buildDate(ctx, monthIndex(m[5]), Number(m[4]), m[6])
        : m[2]
          ? buildDate(ctx, month, Number(m[2]), m[6])
          : undefined;
      return makeDateRef(
        ctx,
        m.index,
        m.index + m[0].length,
        last ? spanDates(first, last) : [first]
      );
    },
  },
  // 10/24 · 10/24/2026 · 10/24–10/26 · 10/24-26
  {
    re: /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?(?:\s*(?:-|–|—|to|thru|through)\s*(?:(\d{1,2})\/)?(\d{1,2})(?:\/(\d{2,4}))?)?\b/gi,
    make: (m, ctx) => {
      const first = buildDate(ctx, Number(m[1]), Number(m[2]), m[3]);
      if (!first) {
        return undefined;
      }
      let dates = [first];
      if (m[5]) {
        const last = buildDate(
          ctx,
          m[4] ? Number(m[4]) : Number(m[1]),
          Number(m[5]),
          m[6] ?? m[3]
        );
        if (last) {
          dates = spanDates(first, last);
        }
      }
      return makeDateRef(ctx, m.index, m.index + m[0].length, dates);
    },
  },
  // the 11th · 12th-15th (day of the month, within the show's dates)
  {
    re: /\b(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)\b(?:\s*(?:-|–|—|to|thru|through)\s*(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)\b)?/gi,
    make: (m, ctx) => {
      const first = ctx.dates.filter((d) => parseIso(d).d === Number(m[1]));
      if (first.length !== 1) {
        return undefined;
      }
      let dates = first;
      if (m[2]) {
        const last = ctx.dates.filter((d) => parseIso(d).d === Number(m[2]));
        if (last.length !== 1) {
          return undefined;
        }
        dates = spanDates(first[0], last[0]);
      }
      return makeDateRef(ctx, m.index, m.index + m[0].length, dates);
    },
  },
  // Thanksgiving, Christmas Day, New Year's Day…
  {
    re: /\b(thanksgiving(?:\s+day)?|christmas\s+(?:day|eve)|xmas\s+day|new\s+year'?s\s+(?:day|eve)|independence\s+day|(?:the\s+)?fourth\s+of\s+july|july\s+4th|labor\s+day|memorial\s+day|veterans\s+day)\b/gi,
    make: (m, ctx) => {
      const dates = [ctx.baseYear - 1, ctx.baseYear, ctx.baseYear + 1]
        .map((year) => holidayDate(m[1], year))
        .filter((date): date is string => !!date);
      const inShow = dates.filter((date) => ctx.dateSet.has(date));
      return makeDateRef(
        ctx,
        m.index,
        m.index + m[0].length,
        inShow.length ? inShow : dates.slice(1, 2)
      );
    },
  },
  // M–F · Tu–Th (capitalised short names)
  {
    re: /\b(M|Tu|W|Th|F|Sa|Su)\s*(?:-|–|—|to|thru|through)\s*(M|Tu|W|Th|F|Sa|Su)\b/g,
    make: (m, ctx) => {
      const first = SHORT_DAYS[m[1]];
      const last = SHORT_DAYS[m[2]];
      const days: number[] = [];
      for (let i = first; ; i = (i + 1) % 7) {
        days.push(i);
        if (i === last || days.length > 7) {
          break;
        }
      }
      return weekdayRef(ctx, m, days);
    },
  },
  {
    re: /\b(Tu|Th|Sa|Su)\b\.?/g,
    make: (m, ctx) => weekdayRef(ctx, m, [SHORT_DAYS[m[1]]]),
  },
  // first / last day
  {
    re: /\b(?:opening|first)\s+day\b|\bday\s*(?:1|one)\b/gi,
    make: (m, ctx) => ({
      ...makeDateRef(
        ctx,
        m.index,
        m.index + m[0].length,
        ctx.dates.slice(0, 1)
      ),
      binding: true,
    }),
  },
  {
    re: /\b(?:last|final|closing)\s+day\b/gi,
    make: (m, ctx) => ({
      ...makeDateRef(ctx, m.index, m.index + m[0].length, ctx.dates.slice(-1)),
      binding: true,
    }),
  },
  // Mon–Thu · Monday through Thursday
  {
    re: new RegExp(
      `\\b(${WEEKDAY_RE})\\s*(?:-|–|—|to|thru|through)\\s*(${WEEKDAY_RE})(?![a-z])`,
      'gi'
    ),
    make: (m, ctx) => {
      const first = weekdayIndex(m[1]);
      const last = weekdayIndex(m[2]);
      if (first < 0 || last < 0) {
        return undefined;
      }
      const days: number[] = [];
      for (let i = first; ; i = (i + 1) % 7) {
        days.push(i);
        if (i === last || days.length > 7) {
          break;
        }
      }
      return weekdayRef(ctx, m, days);
    },
  },
  {
    make: (m, ctx) => weekdayRef(ctx, m, [0, 1, 2, 3, 4, 5, 6], 0),
    re: /\b(?:all\s+other\s+days|every\s+other\s+day|(?:the\s+)?rest\s+of\s+the\s+(?:days|week|show)|(?:all\s+)?(?:the\s+)?remaining\s+days|other\s+days|daily|every\s*day|each\s+day|all\s+days|all\s+week|7\s+days(?:\s+a\s+week)?|every\s+show\s+day)\b/gi,
  },
  {
    re: /\bweek\s*(?:days|nights)\b/gi,
    make: (m, ctx) => weekdayRef(ctx, m, [1, 2, 3, 4, 5]),
  },
  {
    re: /\bweek\s*ends?\b/gi,
    make: (m, ctx) => weekdayRef(ctx, m, [0, 6]),
  },
  {
    re: new RegExp(`\\b(${WEEKDAY_RE})(?![a-z])`, 'gi'),
    make: (m, ctx) => {
      const day = weekdayIndex(m[1]);
      return day < 0
        ? undefined
        : { ...weekdayRef(ctx, m, [day]), label: true };
    },
  },
  // "3pm - close", "10am till late": no closing time
  {
    re: new RegExp(
      `(${CLOCK_RE})\\s*(?:-|–|—|to|until|till|til)\\s*(?:close|closing|late|dusk|sundown|sunset)\\b`,
      'gi'
    ),
    make: (m) => {
      const raw = readRawTime(m[1]);
      return actionToken(m, {
        kind: 'range',
        error: 'No closing time — enter it by hand',
        from: raw ? resolveOpening(raw) : undefined,
      });
    },
  },
  // Time ranges: 10am – 6pm · 10–6 · noon to midnight · 10:00–18:00
  {
    re: new RegExp(
      `(?:\\bfrom\\s+)?(${CLOCK_RE})${RANGE_SEP}(${CLOCK_RE})`,
      'gi'
    ),
    make: (m, ctx) => {
      const dashDate = dashDateOf(m, ctx);
      if (dashDate && ctx.dateSet.has(dashDate)) {
        // "Friday 10-23", "Friday 12-18" (a Dec 18–20 show): a show date,
        // not hours.
        return makeDateRef(ctx, m.index, m.index + m[0].length, [dashDate]);
      }
      if (dashDate) {
        // "Fri 10-9" when Oct 9 is a Friday: a date or hours? Never guess.
        return actionToken(m, {
          kind: 'range',
          error: `Is "${m[0].trim()}" a date or hours? — check the text`,
        });
      }
      if (noMeridiemUnsure(m[1], m[2])) {
        return actionToken(m, { kind: 'range', error: NO_MERIDIEM });
      }
      const range = resolveRange(m[1], m[2]);
      // "Booths 30-45": not times at all, so not a token.
      return range.from === undefined
        ? undefined
        : actionToken(m, { kind: 'range', ...range });
    },
  },
  {
    make: (m) => actionToken(m, { kind: 'closed' }),
    re: /\b(?:closed|dark|no\s+show|not\s+open)\b/gi,
  },
  {
    make: (m) => actionToken(m, { raw: m[1], kind: 'close' }),
    re: new RegExp(
      `\\b(?:closes?|closing|until|till|ends?)(?:\\s+early)?\\s*(?:at|@)?\\s*(${CLOCK_RE})`,
      'gi'
    ),
  },
  // "last day 4pm close"
  {
    make: (m) => actionToken(m, { raw: m[1], kind: 'close' }),
    re: new RegExp(`(${CLOCK_RE})\\s*(?:close|closing)\\b`, 'gi'),
  },
  {
    make: (m) => actionToken(m, { raw: m[1], kind: 'open' }),
    re: new RegExp(
      `\\b(?:opens?|opening|starts?|doors)\\s*(?:at|@)?\\s*(${CLOCK_RE})`,
      'gi'
    ),
  },
  {
    re: /\b(?:except|excluding|but\s+not)\b/gi,
    make: (m) => ({
      kind: 'except',
      start: m.index,
      end: m.index + m[0].length,
    }),
  },
];

const BARE_TIME_RE =
  /^\s*(?:from\s+)?\d{1,2}\s*(?:-|–|—|‒|―|~|to|until|till|til|thru|through)\s*\d{1,2}\s*$/i;
const HOURS_WORD_RE = /\b(?:hours?|open|daily|show|public)\b/i;

const tokenize = (line: string, ctx: Context): Token[] => {
  ctx.line = line;
  const taken: boolean[] = new Array(line.length).fill(false);
  const tokens: Token[] = [];

  MATCHERS.forEach(({ re, make }) => {
    re.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(line))) {
      const start = match.index;
      const end = start + match[0].length;
      if (match[0].length === 0) {
        re.lastIndex++;
        continue;
      }
      const before = line[start - 1];
      if (taken.slice(start, end).some(Boolean)) {
        // "Oct 23 — 12pm-8pm": "23 — 12pm" overlaps the date; look again
        // from the next character so "12pm-8pm" is still found.
        re.lastIndex = start + 1;
        continue;
      }
      if (before && /[$\d.,/#]/.test(before) && /^\d/.test(match[0])) {
        continue;
      }
      const token = make(match, ctx);
      if (!token) {
        continue;
      }
      for (let i = start; i < end; i++) {
        taken[i] = true;
      }
      tokens.push(token);
    }
  });

  tokens.sort((a, b) => a.start - b.start);

  // A weekday written next to a full date only labels it: "Friday, Oct 24",
  // "Oct 24 (Fri)".
  const merged = tokens.filter((token, i) => {
    if (token.kind !== 'ref' || !token.label) {
      return true;
    }
    const next = tokens[i + 1];
    const prev = tokens[i - 1];
    // "Friday, Oct 24", "Friday – October 24", "Fri / Oct 24": a label
    // (its weekday is checked against the date).
    const nextIsDate =
      next?.kind === 'ref' &&
      next.level === 2 &&
      /^[\s,.(\-–—/]*$/.test(line.slice(token.end, next.start));
    const prevIsDate =
      prev?.kind === 'ref' &&
      prev.level === 2 &&
      /^[\s,.(\-–—/]*$/.test(line.slice(prev.end, token.start));
    if (nextIsDate || prevIsDate) {
      ctx.labels.push({
        end: token.end,
        start: token.start,
        date: (nextIsDate ? next : prev) as RefToken,
        day: weekdayIndex(line.slice(token.start, token.end)),
      });
      return false;
    }
    return true;
  });

  // "Fri Oct 23 – Sun Oct 25", "2026-10-23 to 2026-10-25": two dates
  // joined by a dash or "to" are one date range.
  const RANGE_GAP_RE = new RegExp(
    `^[\\s,.]*(?:${WEEKDAY_RE}[\\s,.]*)?(?:-|–|—|to)[\\s,.]*(?:${WEEKDAY_RE}[\\s,.]*)?$`,
    'i'
  );
  for (let i = 0; i < merged.length - 1; i++) {
    const a = merged[i];
    const b = merged[i + 1];
    if (
      a.kind === 'ref' &&
      b.kind === 'ref' &&
      a.level === 2 &&
      b.level === 2 &&
      a.named.length === 1 &&
      b.named.length === 1 &&
      b.named[0] > a.named[0] &&
      RANGE_GAP_RE.test(line.slice(a.end, b.start))
    ) {
      merged.splice(
        i,
        2,
        makeDateRef(ctx, a.start, b.end, spanDates(a.named[0], b.named[0]))
      );
    }
  }

  // "3-4" with no am/pm and no colon is a time only on a line about hours.
  const hasRef = merged.some((token) => token.kind === 'ref');
  return merged.filter(
    (token) =>
      !(
        token.kind === 'action' &&
        token.action.kind === 'range' &&
        BARE_TIME_RE.test(line.slice(token.start, token.end)) &&
        // A certain show reading ("11-5") stays a time, so a line like
        // "Snuday 11-5" is flagged, never ignored; "kids 3-12" is not one.
        !!token.action.error &&
        line.slice(token.start, token.end).trim() !== line.trim() &&
        !hasRef &&
        !HOURS_WORD_RE.test(line)
      )
  );
};

// ------------------------------------------------------------ clauses --

const SETUP_RE =
  /\b(set[\s-]?up|move[\s-]?in|load[\s-]?in|tear[\s-]?down|move[\s-]?out|load[\s-]?out|break[\s-]?down|install(?:ation)?|dismantl\w*|exhibitor\s+(?:arrival|check[\s-]?in|access|hours?))\b/i;

const SETUP_LABELS: [RegExp, string][] = [
  [/^set/i, 'Setup'],
  [/^move[\s-]?in/i, 'Move-in'],
  [/^load[\s-]?in/i, 'Load-in'],
  [/^tear/i, 'Teardown'],
  [/^move[\s-]?out/i, 'Move-out'],
  [/^load[\s-]?out/i, 'Load-out'],
  [/^break/i, 'Breakdown'],
  [/^install/i, 'Setup'],
  [/^dismantl/i, 'Teardown'],
  [/^exhibitor\s+(?:arrival|check)/i, 'Move-in'],
  [/^exhibitor/i, 'Exhibitor hours'],
];

const setupLabel = (keyword: string) =>
  SETUP_LABELS.find(([re]) => re.test(keyword))?.[1] ?? 'Setup';

const PUBLIC_HEADER_RE =
  /\b(?:public|show|event|fair|expo|selling|open(?:ing)?)\s+(?:hours?|times?|schedule)\b|^\s*hours?\s*:?\s*$/i;

const formatNote = (label: string, line: string, tokens: Token[]) => {
  const refs = tokens.filter(
    (token): token is RefToken => token.kind === 'ref' && token.level === 2
  );
  const range = tokens.find(
    (token): token is ActionToken =>
      token.kind === 'action' && token.action.kind === 'range'
  );
  // A weekday that is exactly one show date prints as that date
  // ("Friday: Setup 7am-9am" → "Setup: Fri 23 Oct, …").
  const weekdays = tokens.filter(
    (token): token is RefToken =>
      token.kind === 'ref' && token.level === 1 && !!token.isWeekday
  );
  const named = [
    ...refs.flatMap((ref) => ref.named),
    ...(weekdays.every((ref) => ref.dates.length === 1)
      ? weekdays.flatMap((ref) => ref.dates)
      : []),
  ];
  if (
    named.length &&
    range?.action.kind === 'range' &&
    range.action.from !== undefined &&
    range.action.to !== undefined
  ) {
    // A date named twice ("Wed Aug 5 … (Wed Aug 5)") prints once.
    const unique = Array.from(new Set(named)).sort();
    const days =
      unique.length > 2
        ? `${formatShowDate(unique[0])} – ${formatShowDate(unique[unique.length - 1])}`
        : unique.map(formatShowDate).join(', ');
    return `${label}: ${days}, ${formatMinutes(range.action.from)} – ${formatMinutes(range.action.to)}`;
  }
  const rest = line
    .replace(SETUP_RE, '')
    .replace(/^[\s:,.\-–—]+/, '')
    .replace(/[\s:,;.\-–—]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return rest ? `${label}: ${rest}` : '';
};

// A comma group that is about the public's hours ("…, Show 10am-6pm").
const GROUP_PUBLIC_RE =
  /\b(?:show|public|open|opens|selling|exhibit\s+hall|hours?)\b/i;

interface Group {
  text: string;
  tokens: Token[];
}

/**
 * A clause split at commas into groups that each carry their own days and
 * times ("10am-6pm daily, Sunday 11am-5pm"). A comma starts a new group
 * only before a day, an "except", or a setup / public-hours word ("(" too:
 * "10-6 (Sun 11-5)"), so a time
 * range ("10am – 6pm") and "Friday, October 23" stay whole; a group with
 * no time ("Fri, Sat & Sun 10-6": "Fri") joins the next one (the last
 * joins the one before).
 */
const splitGroups = (line: string, tokens: Token[]): Group[] => {
  // Each cut: the token that starts a group and where its text starts.
  const cuts: { i: number; pos: number }[] = [];
  let lastAction = -1;
  tokens.forEach((token, i) => {
    if (i > 0) {
      const prev = tokens[i - 1];
      const gap = line.slice(prev.end, token.start);
      const close = gap.indexOf(')');
      const open = gap.indexOf('(');
      const comma = gap.indexOf(',');
      const prevInBracket =
        prev.kind === 'ref' &&
        line
          .slice(lastAction >= 0 ? tokens[lastAction].end : 0, prev.start)
          .includes('(');
      if (close >= 0 && prevInBracket && lastAction >= 0) {
        // "10 AM - 6 PM (Fri-Sat), 10 AM - 5 PM (Sun)": a bracket of days
        // belongs to the time before it; the group ends after it.
        cuts.push({ i, pos: prev.end + close + 1 });
      } else if (open >= 0 && bracketHasAction(line, tokens, prev.end + open)) {
        // "10-6 (Sun 11-5)": the bracket holds its own day and time.
        cuts.push({ i, pos: prev.end + open });
      } else if (
        comma >= 0 &&
        !insideBracket(line, prev.end + comma) &&
        (token.kind === 'ref' ||
          token.kind === 'except' ||
          /[a-z]{2,}/i.test(gap.slice(comma + 1)))
      ) {
        cuts.push({ i, pos: prev.end + comma + 1 });
      }
    }
    if (token.kind === 'action') {
      lastAction = i;
    }
  });
  if (!cuts.length) {
    return [{ tokens, text: line }];
  }
  const bounds = [{ i: 0, pos: 0 }, ...cuts];
  const end = { i: tokens.length, pos: line.length };
  // A group with no time joins its neighbour, unless it changes the hours
  // in words the reader can't read ("Sunday open until about 4"): that one
  // stays alone so its days are flagged.
  const hasAction = (k: number) => {
    const from = bounds[k];
    const to = bounds[k + 1] ?? end;
    return (
      tokens.slice(from.i, to.i).some((t) => t.kind === 'action') ||
      CHANGE_WORDS_RE.test(line.slice(from.pos, to.pos))
    );
  };
  const starts: { i: number; pos: number }[] = [];
  let carry: { i: number; pos: number } | undefined;
  bounds.forEach((bound, k) => {
    const from = carry ?? bound;
    if (!hasAction(k)) {
      carry = from;
      return;
    }
    carry = undefined;
    starts.push(from);
  });
  if (!starts.length) {
    return [{ tokens, text: line }];
  }
  // A trailing group with no time joins the one before it.
  return starts.map((from, k) => {
    const to = starts[k + 1] ?? end;
    const start = k === 0 ? 0 : from.pos;
    return {
      text: line.slice(start, to.pos),
      tokens: tokens.slice(k === 0 ? 0 : from.i, to.i).map((t) => ({
        ...t,
        end: t.end - start,
        start: t.start - start,
      })),
    };
  });
};

/** `pos` is inside an open bracket ("(Fri, Sat)"). */
const insideBracket = (line: string, pos: number) =>
  line.slice(0, pos).lastIndexOf('(') > line.slice(0, pos).lastIndexOf(')');

/** The bracket opening at `pos` holds a time ("(Sun 11-5)"). */
const bracketHasAction = (line: string, tokens: Token[], pos: number) => {
  const close = line.indexOf(')', pos);
  const end = close < 0 ? line.length : close;
  return tokens.some(
    (t) => t.kind === 'action' && t.start > pos && t.end <= end
  );
};

// A line worth reporting as not understood: it holds a clock time.
const hasTimeWords = (line: string) =>
  /\d{1,2}(?::\d{2})?\s*(?:a\.?m\b|p\.?m\b|[ap]\b)|\d{1,2}:\d{2}|\bnoon\b|\bmidnight\b/i.test(
    line
  );

// Periods that don't end a sentence: "a.m.", "p.m.", "e.g.", and
// abbreviations ("Sept.", "Oct.", "Fri.", "St.", "No.") followed by a digit
// or a lower-case word. "10.30" and
// "www.site.com" have no space after the period, so they never split.
const ABBREVIATION_RE =
  /\b(jan|feb|mar|apr|jun|jul|aug|sept?|oct|nov|dec|mon|tues?|wed|thu|thur|thurs|fri|sat|sun|st|no|nos|ave|blvd|rd|dr|mr|mrs|ms|mt|ft|approx|vs|etc|est|hrs?|mins?|bldg|ste|dept)\./gi;

const CONTINUES_RE = new RegExp(
  `^\\s+(?:${WEEKDAY_RE}|${MONTH_RE}|daily|every|week)`,
  'i'
);

/**
 * "Fri 10-6 Sat 10-6 Sunday 11-5 Load-in Thursday 2-8": a setup word after
 * a time starts its own clause (the load-in goes to notes, the hours stay
 * hours). A comma before it is handled by the comma groups instead.
 */
const splitAtSetup = (part: string): string[] => {
  const re = new RegExp(SETUP_RE.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(part))) {
    const before = part.slice(0, match.index);
    if (match.index > 0 && /\d/.test(before) && !/,\s*$/.test(before)) {
      return [before, ...splitAtSetup(part.slice(match.index))];
    }
  }
  return [part];
};

interface Clause {
  text: string;
  blank: boolean;
  /** The pasted line it came from. */
  lineNo: number;
  /** The clause is a whole pasted line (a heading can carry to the next). */
  wholeLine: boolean;
}

/**
 * Pasted text as clauses: lines, then ";", "|", "•" and sentence ends
 * (". ", "! ", "? ") inside a line, so a paragraph reads like the same text
 * on separate lines (Dev bug 22 Oct: a one-paragraph paste filled nothing).
 */
const splitClauses = (text: string): Clause[] =>
  text
    .replace(/\r/g, '')
    .split('\n')
    .flatMap((rawLine, lineNo): Clause[] => {
      const line = rawLine
        .replace(/\u00a0/g, ' ')
        // A pasted table "Friday<tab>10:00 AM<tab>6:00 PM": two clock times
        // in neighbouring cells are a range.
        .replace(
          /(\d{1,2}(?::\d{2})?\s*[ap]\.?\s?m\.?)[ \t]*\t[ \t]*(\d{1,2}(?::\d{2})?\s*[ap]\.?\s?m\.?)/gi,
          '$1 - $2'
        )
        .replace(/\t/g, '  ')
        // "a.m." → "am", but "…6 p.m. Show hours…" still ends a sentence
        // ("…9 p.m. Mon.–Sat." does not: a day or month name goes on).
        .replace(
          /\b([ap])\.\s?m\./gi,
          (match, letter: string, offset: number, whole: string) => {
            const next = whole.slice(offset + match.length);
            return /^\s+[A-Z]/.test(next) && !CONTINUES_RE.test(next)
              ? `${letter}m.`
              : `${letter}m`;
          }
        )
        .replace(/\b(e)\.g\./gi, '$1g')
        .replace(/\b(i)\.e\./gi, '$1e')
        // "Sept. 5", "Fri. 10/23", "St. Paul": the period goes; "…Mon. Closed
        // Tue." still ends a sentence (a capital follows).
        .replace(
          ABBREVIATION_RE,
          (match, word: string, offset: number, whole: string) => {
            const next = whole.slice(offset + match.length).trimStart();
            // "Fri. & Sat.", "Sat./Sun." go on too.
            // "Fri. Oct. 23" too: a day or month name follows.
            return /^[0-9a-z(,&/\-–—]/.test(next) ||
              CONTINUES_RE.test(` ${next}`)
              ? word
              : match;
          }
        )
        .trim();
      if (!line) {
        return [{ lineNo, text: '', blank: true, wholeLine: true }];
      }
      const parts = line
        .split(/[;|•·]|[.!?](?=\s|$)/)
        .flatMap(splitAtSetup)
        .map((part) => part.trim())
        .filter(Boolean);
      return parts.map((part) => ({
        lineNo,
        text: part,
        blank: false,
        wholeLine: parts.length === 1,
      }));
    });

// ------------------------------------------------------- strict grammar --
// THE GRAMMAR (owner / coordinator, 23 Oct, sixth pass). A clause fills
// rows only when it is exactly
//     [days | day range | date | date range | "daily" / "every day" /
//      "each day"]  +  [time range | "closed"]
// (either order; several such pairs in a row, all in the same order).
// Connectors: , & and : - – — to / and spaces. A clause may start with the
// no-op prefix "Hours", "Open" or "Show hours". Anything else — except,
// until, till, only, first, last, opening, closing, other, not, no, after,
// late, early, "closes at", short names (M/W/F, Tu, Sa), weekends, a holiday,
// "the 11th", a number that isn't a time or date, any other word — makes
// the clause unclear: it fills nothing and its days (or, with no day, every
// date) are flagged.

const DAY_WORD_RE =
  /^(?:sun|mon|tue|wed|thu|fri|sat|thur|thurs|(?:sun|mon|tues|wednes|thurs|fri|satur)days?)$/i;
const MONTH_WORD_RE =
  /^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)$/i;
const PREFIX_RE = /^\s*(?:show\s+hours|public\s+hours|hours|open)\b\s*:?/i;
const CONNECTOR_TEXT_RE = /^[\s,&:\-–—‒/]*$/;

interface Label {
  end: number;
  day: number;
  start: number;
  date: RefToken;
}

/** Why a token is not part of the grammar, or undefined when it is. */
const tokenProblem = (line: string, token: Token): string | undefined => {
  const text = line.slice(token.start, token.end).trim();
  if (token.kind === 'except') {
    return `"${text}" is not read`;
  }
  if (token.kind === 'action') {
    const { action } = token;
    if (action.kind === 'closed') {
      return /^closed$/i.test(text) ? undefined : `"${text}" is not read`;
    }
    if (action.kind !== 'range') {
      return `"${text}" is not read`;
    }
    if (/\b(?:from|until|till|til|thru|through)\b|~/i.test(text)) {
      return `"${text}" is not read`;
    }
    if (action.error) {
      return action.error;
    }
    if (action.from === undefined || action.to === undefined) {
      return 'Times not understood';
    }
    if (action.from === 0) {
      return 'Opens at midnight — check the hours';
    }
    if (action.to <= action.from) {
      return PAST_MIDNIGHT;
    }
    if (!onGrid(action.from) || !onGrid(action.to)) {
      return OFF_GRID_MESSAGE;
    }
    return undefined;
  }
  if (token.level === 0) {
    return /^(?:daily|every\s+day|each\s+day)$/i.test(text)
      ? undefined
      : `"${text}" is not read`;
  }
  if (token.level === 1) {
    const words = text
      .split(/[\s\-–—‒.]+/)
      .filter((w) => w && !/^to$/i.test(w));
    return words.length && words.every((w) => DAY_WORD_RE.test(w))
      ? undefined
      : `"${text}" is not read`;
  }
  // A date or date range: digits, month names, ordinal endings, connectors.
  const words = (
    text.replace(/(\d)(?:st|nd|rd|th)\b/gi, '$1').match(/[a-z]+/gi) ?? []
  ).filter((w) => !/^to$/i.test(w));
  // A weekday inside a date range is a label ("Fri Oct 23 – Sun Oct 25"),
  // checked against its date.
  return /\d/.test(text) &&
    words.every((w) => MONTH_WORD_RE.test(w) || DAY_WORD_RE.test(w))
    ? undefined
    : `"${text}" is not read`;
};

/** Why a clause is not the grammar, or undefined when it is. */
const clauseProblem = (
  line: string,
  tokens: Token[],
  labels: Label[]
): string | undefined => {
  const covered = new Array(line.length).fill(false);
  const cover = (start: number, end: number) => {
    for (let i = start; i < end; i++) {
      covered[i] = true;
    }
  };
  const prefix = line.match(PREFIX_RE);
  if (prefix) {
    cover(0, prefix[0].length);
  }
  tokens.forEach((t) => cover(t.start, t.end));
  labels.forEach((l) => cover(l.start, l.end));
  const rest = line
    .split('')
    .map((ch, i) => (covered[i] ? ' ' : ch))
    .join('')
    .replace(/\b(?:and|to)\b/gi, ' ');
  if (!CONNECTOR_TEXT_RE.test(rest)) {
    const word =
      rest
        .trim()
        .split(/[\s,&:\-–—‒/]+/)
        .filter(Boolean)[0] ?? rest.trim();
    return `"${word}" is not read`;
  }
  for (const token of tokens) {
    const problem = tokenProblem(line, token);
    if (problem) {
      return problem;
    }
  }
  for (const label of labels) {
    const named = label.date.named;
    if (named.length !== 1 || weekdayOf(named[0]) !== label.day) {
      return `${WEEKDAYS[label.day][0].toUpperCase()}${WEEKDAYS[label.day].slice(1)} doesn't match ${named.length ? formatShowDate(named[0]) : 'the date'}`;
    }
  }
  return undefined;
};

interface Unit {
  action: Action;
  refs: RefToken[];
}

/**
 * The clause's days bound to its times: days first ("Fri 10-6, Sat 11-5")
 * or time first ("10-6 Fri & Sat, 11-5 Sun"), the same order throughout a
 * comma part. A part that is only days joins the next ("Fri, Sat & Sun
 * 10-6"). Undefined when the binding is not exactly that.
 */
const bindUnits = (line: string, tokens: Token[]): Unit[] | undefined => {
  const parts: Token[][] = [[]];
  tokens.forEach((token, i) => {
    if (i > 0 && line.slice(tokens[i - 1].end, token.start).includes(',')) {
      parts.push([]);
    }
    parts[parts.length - 1].push(token);
  });
  // A comma part that is only days joins the part it can belong to: the
  // next one when that starts with days ("Fri, Sat & Sun 10-6"), the one
  // before when that starts with a time ("10-6 Fri, Sat, 11-5 Sun"). Both
  // ("10-6 Fri, Sat, Sun closed") or neither → unclear.
  const isDays = (part: Token[]) => part.every((t) => t.kind === 'ref');
  const joined: Token[][] = [];
  for (let k = 0; k < parts.length; k++) {
    if (!isDays(parts[k])) {
      joined.push([...parts[k]]);
      continue;
    }
    const run: Token[] = [];
    while (k < parts.length && isDays(parts[k])) {
      run.push(...parts[k]);
      k++;
    }
    const prev = joined[joined.length - 1];
    const next = parts[k];
    const back = !!prev && prev[0].kind === 'action';
    // Forward also into a part that is only a time ("Friday, Oct 23, 10am–
    // 6pm").
    const forward =
      !!next &&
      (next[0].kind === 'ref' ||
        (next.length === 1 && next[0].kind === 'action'));
    if (back === forward) {
      return undefined;
    }
    if (forward) {
      joined.push([...run, ...next]);
    } else {
      prev.push(...run);
      k--;
    }
  }
  const units: Unit[] = [];
  for (const part of joined) {
    const bound = daysFirst(line, part) ?? timeFirst(line, part);
    if (
      !bound ||
      // "Friday daily 10-6": every day and one day for the same time.
      bound.some(
        ({ refs }) =>
          refs.some((r) => r.level === 0) && refs.some((r) => r.level !== 0)
      )
    ) {
      return undefined;
    }
    units.push(...bound);
  }
  return units;
};

/**
 * Days of one unit read as one list: "Fri & Sat", "Fri, Sat", "Fri Sat".
 * In a part with several units, days joined by a space only ("Sat 10-6 Fri
 * Sun 11-5") could belong to the time on either side; a colon between days
 * ("Fri: Sat 10-6") isn't a list. Both → not certain.
 */
const listOk = (line: string, refs: RefToken[], several: boolean) =>
  refs.every((ref, i) => {
    if (i === 0) {
      return true;
    }
    const gap = line.slice(refs[i - 1].end, ref.start);
    if (gap.includes(':')) {
      return false;
    }
    return !several || /[,&/]|\band\b/i.test(gap);
  });

const daysFirst = (line: string, part: Token[]): Unit[] | undefined => {
  const units: Unit[] = [];
  let refs: RefToken[] = [];
  for (const token of part) {
    if (token.kind === 'ref') {
      refs.push(token);
    } else if (token.kind === 'action' && refs.length) {
      units.push({ refs, action: token.action });
      refs = [];
    } else {
      return undefined;
    }
  }
  if (refs.length) {
    return undefined;
  }
  return units.every((unit, k) =>
    listOk(line, unit.refs, units.length > 1 && k > 0)
  )
    ? units
    : undefined;
};

const timeFirst = (line: string, part: Token[]): Unit[] | undefined => {
  const units: Unit[] = [];
  for (const token of part) {
    if (token.kind === 'action') {
      if (units.length && !units[units.length - 1].refs.length) {
        return undefined;
      }
      units.push({ refs: [], action: token.action });
    } else if (token.kind === 'ref' && units.length) {
      units[units.length - 1].refs.push(token);
    } else {
      return undefined;
    }
  }
  if (!units.length || !units[units.length - 1].refs.length) {
    return undefined;
  }
  return units.every((unit, k) =>
    listOk(line, unit.refs, units.length > 1 && k < units.length - 1)
  )
    ? units
    : undefined;
};

/**
 * Reads pasted show hours for the given show dates (YYYY-MM-DD, in order).
 */
export const parseShowHours = (
  text: string,
  showDates: string[]
): ShowHoursParseResult => {
  const dates = [...showDates].sort();
  const notes: string[] = [];
  const unread: string[] = [];
  if (!dates.length) {
    return { notes, unread, days: [] };
  }
  const ctx: Context = {
    dates,
    line: '',
    labels: [],
    dateSet: new Set(dates),
    firstDay: dayNumber(dates[0]),
    baseYear: parseIso(dates[0]).y,
    lastDay: dayNumber(dates[dates.length - 1]),
  };
  const allDatesRef: RefToken = {
    dates,
    end: 0,
    start: 0,
    level: 0,
    named: [],
    kind: 'ref',
    outside: [],
  };

  /** Hours read for each date: every-day defaults and day rules apart. */
  // Each value keeps the line it came from (a clash names both lines).
  type Read = { line: string; action: Action };
  const everyDay = new Map<string, Map<string, Read>>();
  const ownDay = new Map<string, Map<string, Read>>();
  const emit = (refs: RefToken[], action: Action, line: string) => {
    const value =
      action.kind === 'range' ? `${action.from}-${action.to}` : 'closed';
    refs.forEach((ref) => {
      const into = ref.level === 0 ? everyDay : ownDay;
      ref.dates.forEach((date) => {
        if (!into.has(date)) {
          into.set(date, new Map());
        }
        if (!into.get(date)!.has(value)) {
          into.get(date)!.set(value, { line, action });
        }
      });
    });
  };
  /**
   * Dates whose hours can't be read for certain: unclear, never guessed.
   * Each keeps every bad line that names it (one reason per line, the
   * first): the row says the first and how many more.
   */
  const flags = new Map<string, { line: string; reason: string }[]>();
  const flag = (refs: RefToken[], line: string, reason: string) => {
    refs.forEach((ref) =>
      ref.dates.forEach((date) => {
        const list = flags.get(date) ?? [];
        if (!list.some((item) => item.line === line.trim())) {
          list.push({ reason, line: line.trim() });
        }
        flags.set(date, list);
      })
    );
  };
  const flagReason = (list: { line: string; reason: string }[]) => {
    const more = list.length - 1;
    return more > 0
      ? `${list[0].reason} —${NBSP}and${NBSP}${more}${NBSP}more${NBSP}line${more > 1 ? 's' : ''}`
      : list[0].reason;
  };
  let setupContext: string | undefined;

  const report = (line: string) => {
    if (!unread.includes(line.trim())) {
      unread.push(line.trim());
    }
  };

  /** A setup / move-in / teardown clause: notes only, never hours. */
  const readSetup = (line: string, tokens: Token[], wholeLine: boolean) => {
    const groups = splitGroups(line, tokens);
    let groupSetup: string | undefined;
    groups.forEach(({ text: part, tokens: partTokens }) => {
      const refs = partTokens.filter((t): t is RefToken => t.kind === 'ref');
      const actions = partTokens.filter((t) => t.kind === 'action');
      const setupMatch = part.match(SETUP_RE);
      const isPublic =
        PUBLIC_HEADER_RE.test(part) || GROUP_PUBLIC_RE.test(part);
      const isSetup =
        !!setupMatch ||
        (!!setupContext && !PUBLIC_HEADER_RE.test(part)) ||
        (!!groupSetup && !isPublic);
      if (!isSetup || (!setupMatch && groupSetup && actions.length)) {
        // A part of a setup clause that may be show hours ("Load-in Thu
        // 2-8, Fri 10-6", "Friday: Setup 7-9, Show 10-6"): never filled.
        const named = refs.filter((ref) => ref.dates.length);
        const why = aboutLine(part, SETUP_OR_SHOW);
        if (named.length) {
          flag(named, part, why);
        } else if (actions.length) {
          flag([allDatesRef], part, why);
        }
        if (!isSetup) {
          groupSetup = undefined;
        }
        return;
      }
      const label = setupMatch
        ? setupLabel(setupMatch[1])
        : (groupSetup ?? setupContext!);
      groupSetup = label;
      if (!refs.length && !actions.length) {
        // "Move-in:" heading on its own line: the next lines belong to it.
        if (wholeLine && groups.length === 1) {
          setupContext = label;
        }
        return;
      }
      const note = formatNote(label, part, partTokens);
      if (note && !notes.includes(note)) {
        notes.push(note);
      }
    });
  };

  /** Any other clause: the strict grammar, or unclear. */
  /** Days-only clause waiting for its time (same line only). */
  let headingDays: { lineNo: number; refs: RefToken[] } | undefined;

  const readClause = (
    line: string,
    tokens: Token[],
    labels: Label[],
    lineNo: number,
    wholeLine: boolean
  ) => {
    const heading = headingDays;
    headingDays = undefined;
    const refs = tokens.filter((t): t is RefToken => t.kind === 'ref');
    const actions = tokens.filter((t) => t.kind === 'action');
    refs.forEach((ref) =>
      ref.outside.forEach((date) => {
        const message = `${formatShowDate(date)} is not one of the show's dates`;
        if (!unread.includes(message)) {
          unread.push(message);
        }
      })
    );
    const named = refs.filter((ref) => ref.dates.length);
    const unclear = (reason: string) => {
      // Every reason names its line (coordinator, 23 Oct).
      const why = aboutLine(line, reason);
      if (
        actions.length ||
        hasTimeWords(line) ||
        /\d/.test(line.replace(/\b\d{4}\b/g, ''))
      ) {
        // A clause with a time or a number we can't read: which dates it
        // covers is not certain ("Fri Oct approximately 23 – Sun Oct 25:
        // 10-6") — every date is unclear.
        flag([allDatesRef], line, why);
      } else if (named.length) {
        // "Sunday cancelled": its days.
        flag(named, line, why);
      } else if (tokens.some((t) => t.kind === 'except')) {
        flag([allDatesRef], line, why);
      }
      if (!named.length && (actions.length || hasTimeWords(line))) {
        report(line);
      }
    };
    const problem = clauseProblem(line, tokens, labels);
    if (problem) {
      unclear(problem);
      if (leftoverWords(line, tokens).some(looksLikeADay)) {
        // "Snuday cancelled", "Fridy 12-8": a day the reader can't name —
        // it could be any date.
        flag([allDatesRef], line, `Not sure which day: ${quoteLine(line)}`);
      }
      return;
    }
    if (!actions.length) {
      // Only days ("Friday, October 23", "October 23–25, 2026"): a heading.
      // A time right after it on the same line is theirs ("Fri, Oct 23 |
      // 10am-6pm").
      headingDays = { refs, lineNo };
      return;
    }
    if (!refs.length && actions.length === 1) {
      if (heading && heading.lineNo === lineNo) {
        emit(heading.refs, (actions[0] as ActionToken).action, line);
        return;
      }
      if (heading) {
        // "Hours: Fri" then "10-6" on the next line: whose time? Never
        // guess.
        flag(
          [allDatesRef],
          line,
          `Not sure which days ${quoteLine(line)} is for`
        );
        report(line);
        return;
      }
      if (!wholeLine) {
        // "Friday closed until noon; open noon-8": a time with no day next
        // to other clauses of its line. Whose? Never guess.
        flag(
          [allDatesRef],
          line,
          `Not sure which days ${quoteLine(line)} is for`
        );
        report(line);
        return;
      }
      // A line that is only a time ("Hours: 10am-6pm"): every show date.
      emit([allDatesRef], (actions[0] as ActionToken).action, line);
      return;
    }
    const units = bindUnits(line, tokens);
    if (!units) {
      unclear(refs.length ? 'which days each time is for' : 'no day');
      return;
    }
    units.forEach(({ action, refs: unitRefs }) => emit(unitRefs, action, line));
  };

  splitClauses(text).forEach(({ blank, lineNo, wholeLine, text: line }) => {
    if (blank) {
      setupContext = undefined;
      return;
    }
    ctx.labels = [];
    const tokens = tokenize(line, ctx);
    if (SETUP_RE.test(line) || (setupContext && !PUBLIC_HEADER_RE.test(line))) {
      headingDays = undefined;
      readSetup(line, tokens, wholeLine);
      return;
    }
    if (PUBLIC_HEADER_RE.test(line)) {
      setupContext = undefined;
    }
    readClause(line, tokens, ctx.labels, lineNo, wholeLine);
  });

  // ------------------------------------------------------------ resolve --
  // ONE RESOLVER: a date's own rules (a weekday, a date, a range) must all
  // agree; they replace an every-day default ("Daily 10-6, Sunday 11-5").
  // Two different every-day times, or two different own times → unclear.
  // No other precedence.
  const days: ParsedShowDay[] = dates.map((date) => {
    const flagged = flags.get(date);
    if (flagged?.length) {
      return {
        date,
        dayOff: false,
        status: 'unclear',
        reason: flagReason(flagged),
      };
    }
    const values = ownDay.get(date) ?? everyDay.get(date);
    if (!values || !values.size) {
      return { date, status: 'unmentioned' };
    }
    if (values.size > 1) {
      const lines = Array.from(
        new Set(Array.from(values.values()).map(({ line }) => line.trim()))
      );
      return {
        date,
        dayOff: false,
        status: 'unclear',
        reason: `${namedLines(lines)}: two different times for this date`,
      };
    }
    const { action } = Array.from(values.values())[0];
    if (action.kind !== 'range') {
      return { date, dayOff: true, status: 'filled' };
    }
    return {
      date,
      dayOff: false,
      status: 'filled',
      to: exactMs(action.to!),
      from: exactMs(action.from!),
    };
  });

  return { days, notes, unread };
};
